// Sistema de notificaciones Toast con animaciones
import { useState, useEffect, useCallback, createContext, useContext } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react'

// Contexto para el sistema de toasts
const ToastContext = createContext(null)

// Iconos y estilos por tipo de toast
const toastConfig = {
  success: {
    icon: CheckCircle2,
    className: 'border-success/30 bg-success/10',
    iconColor: 'text-success',
  },
  error: {
    icon: AlertCircle,
    className: 'border-error/30 bg-error/10',
    iconColor: 'text-error',
  },
  warning: {
    icon: AlertTriangle,
    className: 'border-warning/30 bg-warning/10',
    iconColor: 'text-warning',
  },
  info: {
    icon: Info,
    className: 'border-cyan/30 bg-cyan/10',
    iconColor: 'text-cyan',
  },
}

// Animaciones del toast
const toastVariants = {
  initial: { opacity: 0, y: -20, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', damping: 20, stiffness: 300 } },
  exit: { opacity: 0, x: 100, transition: { duration: 0.2 } },
}

// Componente individual de Toast
function ToastItem({ toast, onRemove }) {
  const { icon: Icon, className, iconColor } = toastConfig[toast.type] || toastConfig.info

  useEffect(() => {
    const timer = setTimeout(() => onRemove(toast.id), toast.duration || 4000)
    return () => clearTimeout(timer)
  }, [toast.id, toast.duration, onRemove])

  return (
    <motion.div
      layout
      variants={toastVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={`
        flex items-start gap-3 p-4 rounded-xl border backdrop-blur-lg
        shadow-lg max-w-sm w-full
        ${className}
      `}
    >
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${iconColor}`} />
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="text-sm font-semibold text-text-primary font-display">{toast.title}</p>
        )}
        <p className="text-sm text-text-secondary">{toast.message}</p>
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        className="p-0.5 rounded text-text-muted hover:text-text-primary transition-colors shrink-0"
        aria-label="Cerrar notificación"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  )
}

// Proveedor del sistema de toasts
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  // Agregar un nuevo toast
  const addToast = useCallback(({ type = 'info', title, message, duration = 4000 }) => {
    const id = Date.now() + Math.random()
    setToasts(prev => [...prev, { id, type, title, message, duration }])
  }, [])

  // Eliminar un toast por su ID
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Atajos para cada tipo de toast
  const toast = {
    success: (message, title) => addToast({ type: 'success', message, title }),
    error: (message, title) => addToast({ type: 'error', message, title }),
    warning: (message, title) => addToast({ type: 'warning', message, title }),
    info: (message, title) => addToast({ type: 'info', message, title }),
  }

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Contenedor fijo para los toasts */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
        <AnimatePresence mode="popLayout">
          {toasts.map(t => (
            <ToastItem key={t.id} toast={t} onRemove={removeToast} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}

// Hook para usar toasts en cualquier componente
export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast debe usarse dentro de un ToastProvider')
  }
  return context
}

export default ToastProvider
